import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import { titleize, humanize } from 'underscore.string';
import { massUpdateActions } from 'constants/MassUpdates';
import { getUpdateFieldForAction } from 'helpers/MassUpdates';
import { withTranslation } from 'react-i18next';
import { useTranslation } from 'react-i18next';
import { sluggify } from 'utils/localization/utils';

const defaultColumnHeaders = [
  { name: 'Order', localizationSlug: 'ORDER' },
  { name: 'Shipper', localizationSlug: 'SHIPPER' },
];

const hideClearAllActions = [massUpdateActions.BULK_CLEAR_TAGS];

function OrdersGroup(props) {
  const {
    updateAction,
    title,
    saving,
    orders,
    disableSave,
    disableClearAll,
    onClearAll,
    onSaveAll,
    onApplyToAll,
    onFieldChange,
    onRemoveOrder,
    updates,
    invalidFields,
  } = props;

  const { t } = useTranslation();

  const getActionName = () => {
    switch (updateAction) {
      case massUpdateActions.SHIPMENT_ARRIVED:
        return t('ARRIVE_ALL');
      case massUpdateActions.BULK_CLEAR_TAGS:
        return t('CLEAR_TAGS');
      default:
        return t('SAVE_ALL');
    }
  };

  const getHeaders = () => {
    const localizationSlug =
      Object.keys(massUpdateActions).find(
        (key) => massUpdateActions[key] === updateAction,
      ) || '';
    const defaultColumnHeadersLocalization = defaultColumnHeaders.map(
      (header) =>
        header.count
          ? t(header.localizationSlug, { count: header.count })
          : t(header.localizationSlug),
    );
    switch (updateAction) {
      case massUpdateActions.UPDATE_ACCOUNTING_REVIEW_DATE:
        return [
          ...defaultColumnHeadersLocalization,
          titleize(t(localizationSlug)),
          t('APPLY_TO_ALL'),
          titleize(t('REMOVE')),
        ];
      default:
        return [...defaultColumnHeadersLocalization];
    }
  };

  const getValidationClass = (order) => {
    // currently one field can be updated
    if (invalidFields.has(order.order_oid)) {
      return { error: true };
    }
    return { error: false };
  };

  const handleRemoveOrder = (order) => () => onRemoveOrder(order);

  const handleClearAll = () => onClearAll();

  const handleApplyToAll = (orderId) => (value) => onApplyToAll(orderId, value);

  const handleSaveAll = () => onSaveAll();

  const handleDateFieldChange = (orderId, field) => (momentDateObj) => {
    const dateString = momentDateObj ? momentDateObj.toISOString() : null;
    onFieldChange(orderId, field, dateString);
  };

  const showClearAll = () => {
    return !hideClearAllActions.includes(updateAction);
  };

  const renderRemoveOrderButton = (order) => {
    const removeKey = `remove_${order.order_oid}`;
    return (
      <td key={removeKey}>
        <button
          className="button button-secondary"
          onClick={handleRemoveOrder(order)}
        >
          <i className="fa fa-remove" />
        </button>
      </td>
    );
  };

  const renderDateInput = (order, field) => {
    let selectedDate;
    if (updates?.[order.order_oid]?.[field] !== undefined) {
      // null or a value need to be shown
      selectedDate = updates?.[order.order_oid]?.[field] ?? '';
    } else {
      // fall back on the original value
      selectedDate = order[field];
    }
    const datePickerKey = `datePicker_${order.order_oid}_${selectedDate}`;
    const className = {
      ...getValidationClass(order, 'date'),
    };

    return (
      <td key={datePickerKey} className={classnames(className)}>
        <div>
          <DatePicker
            key={datePickerKey}
            selected={mapDateValue(selectedDate)}
            placeholderText={t(sluggify(humanize(field)))}
            className="w-full shadow border rounded px-3 -ml-3"
            dateFormat="MM/DD/YYYY"
            onChange={handleDateFieldChange(order.order_oid, field)}
          />
        </div>
      </td>
    );
  };

  const renderApplyToAllButton = ({ order_oid }) => {
    const applyAllKey = `apply_${order_oid}`;
    const buttonClassName = {
      button: true,
      'button-hollow-green': true,
    };

    return (
      <td key={applyAllKey}>
        <button
          type="button"
          disabled={invalidFields.has(order_oid)}
          data-testid={`${applyAllKey}-button`}
          className={classnames(buttonClassName)}
          onClick={handleApplyToAll(order_oid)}
        >
          {titleize(t('APPLY'))}
        </button>
      </td>
    );
  };

  const renderOrderColumns = (order, field) => {
    return [
      ...renderDefaultColumns(order),
      renderDateInput(order, field),
      renderApplyToAllButton(order),
      renderRemoveOrderButton(order),
    ];
  };

  const renderColumns = (order) => {
    switch (updateAction) {
      case massUpdateActions.UPDATE_ACCOUNTING_REVIEW_DATE:
        const field = getUpdateFieldForAction(updateAction);
        return renderOrderColumns(order, field);
      default:
        return renderDefaultColumns(order);
    }
  };

  const renderOrder = (order) => {
    const columns = renderColumns(order);
    return (
      <tr key={order.order_oid} className="p-3">
        {columns}
      </tr>
    );
  };

  const renderHeader = () => {
    const columnHeaders = getHeaders();
    return (
      <thead>
        <tr>
          {columnHeaders.map((col) => (
            <th
              key={col}
              data-testid={`header-${col}`}
              className="font-bold text-grey-darker"
            >
              {col}
            </th>
          ))}
        </tr>
      </thead>
    );
  };

  const renderBody = () => {
    return <tbody>{orders.map((order) => renderOrder(order))}</tbody>;
  };

  const buttonDisabled =
    saving ||
    disableSave ||
    invalidFields.size > 0 ||
    Object.keys(updates).length === 0;

  return (
    <div className="shadow bg-white p-4 mb-6">
      <div
        className="flex justify-between"
        data-testid="mass-update-shipment-modal-title"
      >
        <h4 className="mb-2 text-grey-darker">{title}</h4>
      </div>
      <hr />

      <hr />
      <div className="shipment-rows">
        <table className="table">
          {renderHeader()}
          {renderBody()}
        </table>
        <div className="flex justify-end items-baseline">
          {showClearAll() && (
            <button
              className="button button-hollow-green mr-2"
              onClick={handleClearAll}
              disabled={disableClearAll}
            >
              {t('CLEAR_ALL')}
            </button>
          )}
          <button
            className={`button button-green flex items-center unifiedView-masterList-${updateAction}`}
            disabled={buttonDisabled}
            onClick={handleSaveAll}
            data-testid="mass-update-modal-action-button"
          >
            {saving && (
              <div className="flex items-center mr-2">
                <i className="fa fa-spinner fa-spin" />
              </div>
            )}
            <span>{getActionName()}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

OrdersGroup.propTypes = {
  updateAction: PropTypes.string.isRequired,
  title: PropTypes.string,
  saving: PropTypes.bool,
  orders: PropTypes.array.isRequired,
  disableSave: PropTypes.bool,
  disableClearAll: PropTypes.bool,
  onClearAll: PropTypes.func.isRequired,
  onSaveAll: PropTypes.func.isRequired,
  onApplyToAll: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  onRemoveOrder: PropTypes.func.isRequired,
  updates: PropTypes.object.isRequired,
  invalidFields: PropTypes.object.isRequired,
};

function mapDateValue(value) {
  if (!value) {
    return null;
  }

  if (value instanceof moment) {
    return value;
  }

  return moment(value);
}

function renderDefaultColumns(order) {
  const { order_oid: id } = order;

  return [
    <td key={`order_${id}`} data-orderoid={id}>
      <a href={`/orders/${id}`} target="_blank" rel="noreferrer">
        {order.order_id}
      </a>
    </td>,
    <td key={`shipper_${id}`}>{order.shipper}</td>,
  ];
}

export default withTranslation()(OrdersGroup);
